// ─────────────────────────────────────────────────────────────────────────────
// Canada localisation
//
// Mirrors shared/au-localise.ts. UK is the source of truth; this file
// transforms UK-flavoured strings into Canadian English (closer to US than
// UK) and applies CA rate sanity caps.
//
// Every public function is a no-op when market !== "ca" so UK and AU
// pipelines are byte-identical.
// ─────────────────────────────────────────────────────────────────────────────

// Currency / cost scaling factor used when converting GBP-denominated template
// rates and AI prompt caps into local CAD values.
//
// Calibration: Real Canadian residential build prices sit broadly in line with
// Australian prices (CAD/AUD ≈ 1:1 currency, comparable labour and material
// markets, both ~2× UK delivered cost for the same scope). We therefore use the
// same multiplier as AU rather than the bare GBP→CAD spot rate (~1.7) which
// underprices labour, disposal fees, structural steel and brick veneer in CA.
// Tune against real won quotes.
export const CA_PRICE_FACTOR = 2.0;

// UK term → Canadian term replacements. Order matters: more specific first.
//
// Note on case preservation: rules below mostly use the case-insensitive `gi`
// flag with lowercase replacements (good for mid-sentence body text). Template
// item NAMES are Title Case ("Skip Hire", "Plasterboard", "Skirting Boards"),
// so we register explicit Title Case overrides FIRST. They match before the
// generic lowercase rules and produce "Dumpster Rental", "Drywall", etc.
export const CA_TERMINOLOGY: Array<[RegExp, string | ((m: string) => string)]> = [
  // ── Compound phrases (HIGHEST priority — must run before any token-level
  //    rule that would partially consume them; e.g. without these, "First and
  //    Second Fix" would lose its "Second Fix" substring to the single-token
  //    rule below and the compound regex would no longer match). ──
  [/\bBuilding Regulations Part L\b/gi, "National Building Code (NBC) Part 9.36 (Energy)"],
  [/\bBuilding Regulations Part A\b/gi, "National Building Code (NBC) Part 4 (Structural)"],
  [/\bFirst and Second Fix\b/g, "Rough-In and Trim-Out"],
  [/\bfirst and second fix\b/gi, "rough-in and trim-out"],
  [/\bDot & Dab Plasterboard\b/g, "Adhesive Bonding Drywall"],
  [/\bCeiling Plasterboard\b/g, "Ceiling Drywall"],
  [/\bPlaster Skim Coat\b/g, "Drywall Finishing"],
  // Title Case "Plastering" → "Drywall Finishing" (must come BEFORE the lowercase
  // /\bplastering\b/gi rule on line 96, otherwise that rule matches and produces
  // the lowercase "drywall finishing" — breaking Title Case for item names).
  [/\bPlastering\b/g, "Drywall Finishing"],
  [/\bSkim Coat\b/g, "Drywall Mud / Skim Coat"],
  [/\bT&G Chipboard Flooring\b/g, "T&G Subfloor"],
  [/\bLoft Conversions?\b/g, "Attic Conversion"],
  [/\bon dabs\b/gi, "with adhesive"],
  // UK "inner/outer leaf|skin|wythe|wall" — pair-form FIRST so the bare-form
  // rules below don't half-match. Replacement is lowercase; if the source
  // matched in Title Case, downstream readers don't really mind ("interior
  // wythe" vs "Interior Wythe") in a description body.
  [/\binner and (?:outer|exterior) (?:leaf|skin|wythe|wall)\b/gi, "interior and exterior wythe"],
  [/\b(?:outer|exterior) and inner (?:leaf|skin|wythe|wall)\b/gi, "interior and exterior wythe"],
  [/\binner (?:leaf|skin|wythe|wall)\b/gi, "interior wythe"],
  [/\bouter (?:leaf|skin|wythe|wall)\b/gi, "exterior wythe"],
  [/\bcentral heating circuit\b/gi, "heating supply"],
  // UK cavity-wall concepts that don't exist in Canadian residential
  // construction (wood-frame + brick veneer / siding). Translate to the
  // closest Canadian equivalent so the line at least makes sense if the AI
  // slips them through despite the prompt warnings.
  [/\bDamp Proof Course\b/g, "Capillary Break"],
  [/\bdamp proof course\b/gi, "capillary break"],
  [/\bDPC\b/g, "capillary break"],
  [/\bDPM\b/g, "vapour barrier"],
  [/\bCavity Closers?\b/g, "Air-Sealing at Openings"],
  [/\bcavity closers?\b/gi, "air-sealing at openings"],
  [/\bCavity Trays?\b/g, "Through-Wall Flashing"],
  [/\bcavity trays?\b/gi, "through-wall flashing"],
  [/\bcavity walls?\b/gi, "exterior walls"],
  [/\bCavity Walls?\b/g, "Exterior Walls"],

  // ── Title Case single-token overrides (for template item names) ──
  [/\bSkip Hire\b/g, "Dumpster Rental"],
  [/\bSkip Bins?\b/g, "Dumpsters"],
  [/\bDot & Dab\b/g, "Adhesive Bonding"],
  [/\bPlasterboard\b/g, "Drywall"],
  [/\bSkirting Boards?\b/g, "Baseboards"],
  [/\bSkirting\b/g, "Baseboard"],
  [/\bArchitraves?\b/g, "Casing"],
  [/\bIronmongery\b/g, "Hardware"],
  [/\bPadstones?\b/g, "Bearing Plates"],
  [/\bAcrow Props?\b/g, "Shore Posts"],
  [/\bRainwater Goods\b/g, "Eavestrough System"],
  [/\bHeating Extension\b/g, "Heating Connection"],
  [/\bWindow Boards\b/g, "Window Sills"],
  [/\bT&G Chipboard\b/g, "T&G Subfloor"],
  [/\bChipboard\b/g, "Particleboard"],
  [/\bLofts?\b/g, "Attic"],
  [/\bCloakroom\b/g, "Powder Room"],
  [/\bConsumer Units?\b/g, "Electrical Panel"],
  [/\bFirst Fix\b/g, "Rough-In"],
  [/\bSecond Fix\b/g, "Trim-Out"],
  [/\bSnagging\b/g, "Deficiency List"],
  [/\bTimber\b/g, "Lumber"],

  // ── Generic lowercase rules (mid-sentence body text) ──
  // Materials & finishes
  [/\bplasterboard\b/gi, "drywall"],
  [/\bplastering\b/gi, "drywall finishing"],
  [/\bskim coat\b/gi, "drywall mud / skim coat"],
  [/\bskimming\b/gi, "drywall finishing"],
  [/\bdot and dab\b/gi, "adhesive bonding"],
  [/\bdot & dab\b/gi, "adhesive bonding"],
  [/\brendering\b/gi, "stucco"],
  [/\brender\b/gi, "stucco"],
  [/\bskirting boards?\b/gi, "baseboards"],
  [/\bskirting\b/gi, "baseboard"],
  [/\barchitraves?\b/gi, "casing"],
  [/\bironmongery\b/gi, "hardware"],
  [/\btimber\b/gi, "lumber"],

  // Structural
  [/\bRSJ\b/g, "steel beam (W-section)"],
  [/\bpadstones?\b/gi, "bearing plates"],
  [/\bAcrow props?\b/gi, "shore posts"],
  [/\bstrongboys?\b/gi, "needle beam supports"],
  [/\bstud work\b/gi, "framing"],
  [/\bstudwork\b/gi, "framing"],

  // Approvals & code
  [/\bBuilding Control\b/g, "Building Department"],
  [/\bBuilding Regulations\b/g, "National Building Code (NBC)"],
  [/\bPlanning Permission\b/gi, "Building Permit"],
  [/\bPlanning Application\b/gi, "Building Permit Application"],
  [/\bPart A\b/g, "NBC Part 4 (Structural)"],
  [/\bPart L\b/g, "NBC Part 9.36 (Energy)"],

  // Drainage / plumbing
  [/\bfoul drains?\b/gi, "sanitary drains"],
  [/\bsoil pipes?\b/gi, "soil stacks"],
  [/\bUPVC\b/g, "PVC"],
  [/\btaps?\b/gi, "faucet"],
  [/\bWC\b/g, "toilet"],

  // Roof
  [/\bVelux\b/g, "skylight"],
  [/\blofts?\b/gi, "attic"],
  [/\bloft conversions?\b/gi, "attic conversion"],

  // Trades & sequencing
  [/\bfirst fix\b/gi, "rough-in"],
  [/\bsecond fix\b/gi, "trim-out / finish"],

  // Electrical hardware
  [/\bconsumer units?\b/gi, "electrical panel"],
  [/\bRCDs?\b/g, "GFCI breaker"],
  [/\bRCBOs?\b/g, "AFCI/GFCI breaker"],
  [/\bMCBs?\b/g, "breaker"],

  // Concrete / fill
  [/\bscreed\b/gi, "concrete topping"],
  [/\bhardcore\b/gi, "compacted granular fill"],
  // (DPC and DPM are translated in the compound section above to
  //  "capillary break" and "vapour barrier" respectively. Do not duplicate
  //  here — repeating the rule with a different replacement creates fragile
  //  ordering dependencies. The compound rules win because they run first.)
  [/\bground floor slab\b/gi, "slab on grade"],

  // Insulation
  [/\binsulation quilt\b/gi, "batt insulation"],
  [/\bKnauf\b/g, "CertainTeed / CGC"],

  // Snags / payments
  [/\bsnagging\b/gi, "deficiency list"],
  [/\bprovisional sums?\b/gi, "allowances"],

  // Site / civil
  [/\bblock paving\b/gi, "interlocking pavers"],
  [/\bblock work\b/gi, "concrete block (CMU)"],
  [/\bblockwork\b/gi, "concrete block (CMU)"],
  [/\btarmac\b/gi, "asphalt"],
  [/\bkerbs?\b/gi, "curb"],

  // Waste / equipment
  [/\bskip bins?\b/gi, "dumpster"],
  [/\bskip hire\b/gi, "dumpster rental"],
  [/\bskips?\b/gi, "dumpster"],

  // Misc
  [/\blifts?\b/gi, "elevator"],
  [/\bverandahs?\b/gi, "porch"],
];

// ─────────────────────────────────────────────────────────────────────────────
// Per-category CA rate sanity caps (CAD, supply + install, ex-Sales Tax).
// Catches AI overshoots and template multipliers that go too high.
// Calibrate against real won CA quotes. Keep slightly above realistic top-end.
// ─────────────────────────────────────────────────────────────────────────────
export interface RateCap {
  units: string[];
  maxUnitCost: number;
  /** Optional floor — clamps obvious AI underpricing UP to a realistic CA rate. */
  minUnitCost?: number;
}

export const CA_RATE_CAPS: Record<string, RateCap> = {
  drywall: { units: ["sqm", "m²", "m2", "sqft", "sf"], maxUnitCost: 75, minUnitCost: 28 },          // 1/2" board supply+install
  drywall_finish: { units: ["sqm", "m²", "m2"], maxUnitCost: 60, minUnitCost: 32 },                 // tape & mud Level 4
  decorating: { units: ["sqm", "m²", "m2"], maxUnitCost: 50, minUnitCost: 22 },                     // 2-coat paint
  insulation: { units: ["sqm", "m²", "m2"], maxUnitCost: 60, minUnitCost: 22 },                     // R-22 batts supply+install
  flooring: { units: ["sqm", "m²", "m2"], maxUnitCost: 180 },                                       // laminate to engineered hardwood
  wall_tiling: { units: ["sqm", "m²", "m2"], maxUnitCost: 110 },                                    // wall ceramic/porcelain supply+fix
  floor_tiling: { units: ["sqm", "m²", "m2"], maxUnitCost: 130 },                                   // floor ceramic/porcelain supply+fix
  tiling: { units: ["sqm", "m²", "m2"], maxUnitCost: 130 },                                         // generic tiling fallback
  waterproofing: { units: ["sqm", "m²", "m2"], maxUnitCost: 100 },                                  // tanking / membrane to wet area
  carpentry_trim: { units: ["lm", "m", "linear_m", "linear m", "lin m", "lin_m", "lin. m", "l/m", "linear metre", "linear meter", "linear metres", "linear meters"], maxUnitCost: 70, minUnitCost: 28 }, // baseboard / casing per lm
  brickwork: { units: ["sqm", "m²", "m2"], maxUnitCost: 320, minUnitCost: 240 },                    // brick veneer supply+lay
  windows: { units: ["each", "no", "no.", "nr", "item"], maxUnitCost: 2500, minUnitCost: 800 },     // vinyl window supply+install
  strip_footing: { units: ["lm", "m"], maxUnitCost: 380, minUnitCost: 200 },                        // poured concrete in trench
  bearing_plate: { units: ["each", "no", "no.", "nr", "item"], maxUnitCost: 500 },                  // catch C$2,850 overshoots
  sanitaryware_install: { units: ["item", "each", "no", "no.", "nr"], maxUnitCost: 2200 },          // labour-only full suite
  bath_accessories_install: { units: ["item", "each", "no", "no.", "nr"], maxUnitCost: 1100 },      // suite of 4-5 accessories
  shower_screen_install: { units: ["item", "each", "no", "no.", "nr"], maxUnitCost: 800 },          // labour-only
  towel_rail_install: { units: ["item", "each", "no", "no.", "nr"], maxUnitCost: 750 },             // labour-only
  dumpster: { units: ["item", "each", "no", "no.", "nr"], maxUnitCost: 800 },                       // single bin on small refurb
};

function resolveCapKey(category?: string, name?: string): keyof typeof CA_RATE_CAPS | null {
  const c = (category || "").toLowerCase();
  const n = (name || "").toLowerCase();

  // Order matters — most specific patterns first so they're not pre-empted
  // by broader category matches.
  if (n.includes("bearing plate") || n.includes("padstone")) return "bearing_plate";
  if (n.includes("strip footing") || (n.includes("footing") && (c.includes("foundation") || c.includes("concrete")))) return "strip_footing";
  // Only floor "window" lines that look like SUPPLY+INSTALL of new windows.
  // Skip lines that are repair/removal/protection/board/sill/reveal/film/etc.
  // — those have legitimately low unit costs and shouldn't be floored.
  if (
    n.includes("window") &&
    !n.includes("window board") &&
    !n.includes("window sill") &&
    !n.includes("window reveal") &&
    !n.includes("window film") &&
    !n.includes("window cleaning") &&
    !n.includes("window protection") &&
    !n.includes("window removal") &&
    !n.includes("remove window") &&
    !n.includes("repair") &&
    !n.includes("restoration") &&
    !n.includes("trickle vent")
  ) return "windows";
  if (n.includes("drywall finish") || n.includes("tape and mud") || n.includes("tape & mud") || n.includes("skim coat") || n.includes("mud and tape")) return "drywall_finish";
  if (c.includes("plaster") || c.includes("drywall") || n.includes("drywall") || n.includes("plasterboard")) return "drywall";
  if (c.includes("decorat") || c.includes("paint") || n.includes("painting")) return "decorating";
  if (c.includes("insulat") || n.includes("insulation") || n.includes("batt")) return "insulation";
  // Bathroom labour-only line items (must precede generic "floor"/"plumbing" matches)
  if (n.includes("sanitaryware install") || n.includes("plumbing fixtures install") || n.includes("plumbing fixture install")) return "sanitaryware_install";
  if (n.includes("bathroom accessor") || (n.includes("accessories") && n.includes("install") && n.includes("labour"))) return "bath_accessories_install";
  if (n.includes("shower screen") || n.includes("shower enclosure") || n.includes("shower door")) return "shower_screen_install";
  // Towel-rail cap intentionally requires explicit "towel" context — a generic
  // "Radiator Installation" in a non-bathroom (heating) job has a higher
  // legitimate ceiling and shouldn't be capped at the bathroom labour rate.
  if (n.includes("towel rail") || n.includes("towel warmer") || (n.includes("radiator") && n.includes("install") && (n.includes("towel") || c.includes("bathroom")))) return "towel_rail_install";
  if (n.includes("waterproof") || n.includes("tanking") || n.includes("pan liner")) return "waterproofing";
  if (n.includes("dumpster") || n.includes("disposal bin") || n.includes("waste bin") || n.includes("skip ")) return "dumpster";
  // Tile splits — wall vs floor — must be checked BEFORE the generic "flooring"
  // category check below, otherwise an AI emit of category "floor_tiling" /
  // "floor tiling" returns the broader flooring cap instead of the tile cap.
  if (c.includes("til") && (n.includes("wall til") || n.includes("backsplash"))) return "wall_tiling";
  if ((c.includes("til") || c.includes("floor")) && n.includes("floor til")) return "floor_tiling";
  if (c.includes("til")) return "tiling";
  if (c.includes("floor") && !n.includes("subfloor")) return "flooring";
  if (c.includes("brick") || c.includes("blockwork") || c.includes("masonry")) return "brickwork";
  if (n.includes("baseboard") || n.includes("casing") || n.includes("skirting") || n.includes("architrave")) return "carpentry_trim";
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Categorisation guard — fix common AI miscategorisations under "Preliminaries"
// (mirror of AU implementation, with CA-aware patterns)
// ─────────────────────────────────────────────────────────────────────────────
function fixCategoryIfWrong(item: { name?: string; description?: string; category?: string }): string | undefined {
  const cat = (item.category || "").toLowerCase();
  if (!cat.includes("prelim")) return item.category;

  const blob = `${item.name || ""} ${item.description || ""}`.toLowerCase();
  if (/\b(floor framing|attic floor|floor joists?|joists?|trimmers|hangers|i[- ]?joists?)\b/.test(blob)) return "carpentry";
  if (/\b(stud framing|stud wall|wall framing|partition framing)\b/.test(blob)) return "carpentry";
  if (/\b(steel beam|w[- ]?section|ridge beam|bearing plate|lintel|header)\b/.test(blob)) return "steelwork";
  if (/\b(plant & equipment|lifting equipment|crane|telehandler|small plant)\b/.test(blob)) return "plant_equipment";
  if (/\b(scaffold|edge protection|guardrail)\b/.test(blob)) return "scaffolding";
  if (/\b(dumpster|waste removal|disposal bin)\b/.test(blob)) return "waste";
  if (/\b(demolition|strip[- ]?out|soft strip)\b/.test(blob)) return "demolition";
  return item.category;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────
export function localiseText(text: string, market?: string | null): string {
  if (market !== "ca" || !text) return text;
  let out = text;
  for (const [re, repl] of CA_TERMINOLOGY) {
    out = typeof repl === "function" ? out.replace(re, repl as any) : out.replace(re, repl);
  }
  return out;
}

export function applyCaRateCap<T extends { unitCost?: number; unit?: string; category?: string; name?: string }>(item: T, market?: string | null): T {
  if (market !== "ca" || typeof item.unitCost !== "number") return item;
  const key = resolveCapKey(item.category, item.name);
  if (!key) return item;
  const cap = CA_RATE_CAPS[key];
  const u = (item.unit || "").toLowerCase();
  if (!cap.units.includes(u)) return item;
  if (item.unitCost > cap.maxUnitCost) {
    return { ...item, unitCost: cap.maxUnitCost };
  }
  if (typeof cap.minUnitCost === "number" && item.unitCost > 0 && item.unitCost < cap.minUnitCost) {
    return { ...item, unitCost: cap.minUnitCost };
  }
  return item;
}

export function fixCaCategory<T extends { name?: string; description?: string; category?: string }>(item: T, market?: string | null): T {
  if (market !== "ca") return item;
  const newCat = fixCategoryIfWrong(item);
  if (newCat && newCat !== item.category) return { ...item, category: newCat };
  return item;
}

export function localiseCaLineItems<T extends { name?: string; description?: string; unitCost?: number; unit?: string; category?: string }>(items: T[], market?: string | null): T[] {
  if (market !== "ca" || !Array.isArray(items)) return items;
  return items.map(item => {
    let next: T = { ...item };
    if (typeof next.name === "string") next.name = localiseText(next.name, market) as any;
    if (typeof next.description === "string") next.description = localiseText(next.description, market) as any;
    next = fixCaCategory(next, market);
    next = applyCaRateCap(next, market);
    return next;
  });
}
